import { useState } from 'react';
import { salaryInsightsService } from '../../../services/salaryInsightsService';

export const ZipCodeManager = ({ 
  currentZip, 
  targetZip, 
  activeZip, 
  onActiveZipChange, 
  onZipUpdate, 
  loading 
}) => {
  const [editingCurrent, setEditingCurrent] = useState(false);
  const [editingTarget, setEditingTarget] = useState(false);
  const [tempCurrentZip, setTempCurrentZip] = useState(currentZip || '');
  const [tempTargetZip, setTempTargetZip] = useState(targetZip || '');
  const [errors, setErrors] = useState({});

  const handleCurrentZipSave = async () => {
    if (!salaryInsightsService?.validateZipCode(tempCurrentZip)) {
      setErrors({ current: 'Please enter a valid 5-digit ZIP code' });
      return;
    }

    try {
      await onZipUpdate('current', tempCurrentZip);
      setEditingCurrent(false);
      setErrors({});
    } catch (error) {
      setErrors({ current: 'Failed to update ZIP code' });
    }
  };

  const handleTargetZipSave = async () => {
    if (!salaryInsightsService?.validateZipCode(tempTargetZip)) {
      setErrors({ target: 'Please enter a valid 5-digit ZIP code' });
      return;
    }

    try {
      await onZipUpdate('target', tempTargetZip);
      setEditingTarget(false);
      setErrors({});
    } catch (error) {
      setErrors({ target: 'Failed to update ZIP code' });
    }
  };

  const handleCancel = (type) => {
    if (type === 'current') {
      setTempCurrentZip(currentZip || '');
      setEditingCurrent(false);
    } else {
      setTempTargetZip(targetZip || '');
      setEditingTarget(false);
    }
    setErrors({});
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Location Settings</h3>
      {/* ZIP Code Inputs */}
      <div className="space-y-4">
        {/* Current ZIP */}
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current ZIP Code
            </label>
            {editingCurrent ? (
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={tempCurrentZip}
                  onChange={(e) => setTempCurrentZip(e?.target?.value)}
                  placeholder="12345"
                  maxLength={5}
                  className={`flex-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-primary sm:text-sm ${errors?.current ? 'border-red-300' : ''}`}
                />
                <button
                  onClick={handleCurrentZipSave}
                  disabled={loading}
                  className="px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={() => handleCancel('current')}
                  className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <span className="text-gray-900">
                  {currentZip || 'Not set'}
                </span>
                <button
                  onClick={() => setEditingCurrent(true)}
                  className="text-primary hover:text-primary/80 text-sm font-medium"
                >
                  {currentZip ? 'Edit' : 'Add'}
                </button>
              </div>
            )}
            {errors?.current && (
              <p className="mt-1 text-sm text-red-600">{errors?.current}</p>
            )}
          </div>
        </div>

        {/* Target ZIP */}
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target ZIP Code <span className="text-gray-400">(Optional)</span>
            </label>
            {editingTarget ? (
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={tempTargetZip}
                  onChange={(e) => setTempTargetZip(e?.target?.value)}
                  placeholder="12345"
                  maxLength={5}
                  className={`flex-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-primary sm:text-sm ${errors?.target ? 'border-red-300' : ''}`}
                />
                <button
                  onClick={handleTargetZipSave}
                  disabled={loading}
                  className="px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={() => handleCancel('target')}
                  className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <span className="text-gray-900">
                  {targetZip || 'Not set'}
                </span>
                <button
                  onClick={() => setEditingTarget(true)}
                  className="text-primary hover:text-primary/80 text-sm font-medium"
                >
                  {targetZip ? 'Edit' : 'Add'}
                </button>
              </div>
            )}
            {errors?.target && (
              <p className="mt-1 text-sm text-red-600">{errors?.target}</p>
            )}
          </div>
        </div>
      </div>
      {/* Active ZIP Selector */}
      {(currentZip || targetZip) && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            View salary data for:
          </label>
          <div className="flex space-x-4">
            {currentZip && (
              <label className="flex items-center">
                <input
                  type="radio"
                  value="current"
                  checked={activeZip === 'current'}
                  onChange={(e) => onActiveZipChange(e?.target?.value)}
                  className="focus:ring-primary h-4 w-4 text-primary border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Current ({currentZip})
                </span>
              </label>
            )}
            {targetZip && (
              <label className="flex items-center">
                <input
                  type="radio"
                  value="target"
                  checked={activeZip === 'target'}
                  onChange={(e) => onActiveZipChange(e?.target?.value)}
                  className="focus:ring-primary h-4 w-4 text-primary border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Target ({targetZip})
                </span>
              </label>
            )}
          </div>
        </div>
      )}
      {/* Help Text */}
      <div className="mt-4 p-3 bg-primary/5 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-primary/60" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-primary/80">
              Add your current ZIP code and optionally a target location where you're considering moving.
              Premium users can see detailed local salary data.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};